import React, { useEffect, useState } from 'react';
import '../assets/css/systemManagement.css';
import { GroupsModel } from '../models/GroupsModel';
import { RoleModel, SortModel, SortDirection, ActivityStatus } from '../models/superbit-api-models';
import DeleteAlert from '../components/DeleteAlert';
import NewGroup from './_NewGroup';

// React port of Views/SystemManagement/_Groups.cshtml (+ _GroupsItems.cshtml).
// A plain (un-paged) list of groups/roles with name, member count and status,
// plus add/edit/delete actions. The old view sorted server-side via the
// data-sort-by attributes ("Name"/"Status"), so we keep doing that through
// groupSort and let the server return the ordered list.

type Column = { key: string; label: string; sortBy?: string };
const COLUMNS: Column[] = [
    { key: 'name', label: 'שם קבוצה', sortBy: 'Name' },
    { key: 'usersInRole', label: 'מספר חברים' },
    { key: 'status', label: 'סטטוס', sortBy: 'Status' },
];

const isActive = (s?: ActivityStatus) => s === ActivityStatus._0; // 0 = Active (פעיל)

const GroupsManagement = () => {
    const [groupsModel] = useState<GroupsModel>(() => new GroupsModel());

    const [groups, setGroups] = useState<RoleModel[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const [sortProp, setSortProp] = useState<string | undefined>(undefined);
    const [sortDir, setSortDir] = useState<SortDirection>(SortDirection._0);

    const [openMenuId, setOpenMenuId] = useState<string | undefined>(undefined);
    const [deleteId, setDeleteId] = useState<string | undefined>(undefined);

    // Add/edit group modal (ported _NewGroup). undefined editGroupId = add mode.
    const [showGroupModal, setShowGroupModal] = useState<boolean>(false);
    const [editGroupId, setEditGroupId] = useState<string | undefined>(undefined);

    const buildSort = (): SortModel[] => {
        if (!sortProp) return [];
        const sort = new SortModel();
        sort.property = sortProp;
        sort.sortDirection = sortDir;
        return [sort];
    };

    const load = async () => {
        setLoading(true);
        try {
            const result = await groupsModel.groupSort(buildSort());
            setGroups(result ? result : []);
        } catch (e) {
            console.error('Failed to load groups', e);
            setGroups([]);
        } finally {
            setLoading(false);
        }
    };

    // Reload whenever the sort changes (and on first mount).
    useEffect(() => {
        load();
    }, [sortProp, sortDir]);

    const onSort = (column: Column) => {
        if (!column.sortBy) return;
        if (sortProp === column.sortBy) {
            setSortDir(sortDir === SortDirection._0 ? SortDirection._1 : SortDirection._0);
        } else {
            setSortProp(column.sortBy);
            setSortDir(SortDirection._0);
        }
    };

    const toggleMenu = (id: string | undefined) => {
        if (!id) return;
        setOpenMenuId(openMenuId === id ? undefined : id);
    };

    const onAdd = () => {
        setEditGroupId(undefined);
        setShowGroupModal(true);
    };

    const onEdit = (group: RoleModel) => {
        setOpenMenuId(undefined);
        setEditGroupId(group.id);
        setShowGroupModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        try {
            await groupsModel.groupDelete(deleteId);
            await load();
        } catch (e) {
            console.error('Failed to delete group', e);
        } finally {
            setDeleteId(undefined);
        }
    };

    return (
        <div className="groups-management rtl">
            <div className="users-table-wrapper">
                <table className="users-table">
                    <thead>
                        <tr>
                            {COLUMNS.map((col) => (
                                <th
                                    key={col.key}
                                    className={`users-th ${col.sortBy ? 'sortable pointer' : ''} ${
                                        sortProp === col.sortBy ? 'sorted' : ''
                                    }`}
                                    onClick={() => onSort(col)}
                                >
                                    <span>{col.label}</span>
                                    {col.sortBy && (
                                        <span
                                            className={`arrow-updown ${
                                                sortProp === col.sortBy
                                                    ? sortDir === SortDirection._0
                                                        ? 'asc'
                                                        : 'desc'
                                                    : ''
                                            }`}
                                        />
                                    )}
                                </th>
                            ))}
                            <th className="users-th users-th--add">
                                <div className="add-btn pointer" onClick={onAdd}>
                                    <span className="add-file" />
                                    <span className="text">הוסף</span>
                                </div>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {groups.map((group) => (
                            <tr key={group.id}>
                                <td className="bold">{group.name}</td>
                                <td>{group.usersInRole}</td>
                                <td className={isActive(group.status) ? 'online' : 'offline'}>
                                    <span>{isActive(group.status) ? 'פעיל' : 'לא פעיל'}</span>
                                    <span className="dot" />
                                </td>
                                <td className="users-actions">
                                    <button
                                        type="button"
                                        className="actionBtn pointer"
                                        onClick={() => toggleMenu(group.id)}
                                    >
                                        <div className="edit-icon" />
                                        <span>פעולות</span>
                                        <div className="dropdown-icon" />
                                    </button>
                                    {openMenuId === group.id && (
                                        <ul className="actionBtnMenu">
                                            <li className="actionBtnMenuItem" onClick={() => onEdit(group)}>
                                                ערוך
                                            </li>
                                            <li
                                                className="actionBtnMenuItem"
                                                onClick={() => {
                                                    setOpenMenuId(undefined);
                                                    setDeleteId(group.id);
                                                }}
                                            >
                                                מחק
                                            </li>
                                        </ul>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!loading && groups.length === 0 && (
                            <tr>
                                <td className="users-empty" colSpan={COLUMNS.length + 1}>
                                    לא נמצאו קבוצות
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <DeleteAlert
                show={!!deleteId}
                onClose={() => setDeleteId(undefined)}
                onConfirm={confirmDelete}
            />

            <NewGroup
                show={showGroupModal}
                groupId={editGroupId}
                onClose={() => setShowGroupModal(false)}
                onSaved={() => {
                    setShowGroupModal(false);
                    load();
                }}
            />
        </div>
    );
};

export default GroupsManagement;
export {};
